import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../../styles/theme';
import { formatMetaMoney } from '../metaAdsMoney';
import { metaBudgetSummaries, metaRemainingLabel } from '../metaBudget';
import { formatMetaPct } from '../metaFormat';
import { metaAdSetStatusLabel, metaBidStrategyLabel } from '../metaLabels';
import { formatMetaDay } from '../metaPeriod';
import { metaWriteBudgetOf, readMetaWriteBlock } from '../metaWrite';
import { getMetaAdSets } from '../../services/metaAdsService';
import { useMetaRead } from '../hooks/useMetaRead';
import { LinkMenu, metaWriteItems } from './MetaActionMenu';
import { MetaCard, MetaFootnote, MetaStatRows } from './MetaDataParts';
import { metaAdsBodyTextSx, metaAdsMutedTextSx } from './metaAdsUi.styles';

const money = (amount, currency) => formatMetaMoney(amount, currency);
const row = (key, label, value) => ({ key, label, value });

const campaignsHint = (n, kind) => (n === 1 ? `1 campaña con presupuesto ${kind}` : `${n} campañas con presupuesto ${kind}`);

/**
 * Filas del resumen por moneda: solo las que tienen dato. Lo que queda HOY de los diarios y lo que
 * queda de los totales van separados (nunca sumados); cada línea se oculta si no hay campañas de ese tipo.
 */
function summaryRows(s) {
  const c = s.currency;
  return [
    s.daily != null ? row('daily', 'Presupuesto diario (suma)', money(s.daily, c)) : null,
    s.dailyCampaignsCount > 0 && s.remainingTodaySum != null
      ? { ...row('remaining-today', 'Queda hoy (presupuestos diarios)', money(s.remainingTodaySum, c)), hint: campaignsHint(s.dailyCampaignsCount, 'diario') }
      : null,
    s.lifetime != null ? row('lifetime', 'Presupuesto total (suma)', money(s.lifetime, c)) : null,
    s.lifetimeCampaignsCount > 0 && s.remainingLifetimeSum != null
      ? { ...row('remaining-lifetime', 'Restante de presupuestos totales', money(s.remainingLifetimeSum, c)), hint: campaignsHint(s.lifetimeCampaignsCount, 'total') }
      : null,
    s.spentInPeriod != null ? row('period', 'Gastado en el período', money(s.spentInPeriod, c)) : null,
    s.spentTotal != null ? row('total', 'Gastado desde el inicio', money(s.spentTotal, c)) : null,
  ].filter(Boolean);
}

const remainingText = (amount, currency, source) => `${money(amount, currency)}${source === 'calculated' ? ' · calculado' : ''}`;

/** Filas de una campaña. `full` agrega lo que no entra en la tarjeta cerrada. */
export function campaignBudgetRows(b, full) {
  const c = b.currency;
  const out = [];
  if (b.level === 'campaign') {
    out.push(row('daily', 'Presupuesto diario', b.daily != null ? money(b.daily, c) : 'Sin presupuesto diario'));
    out.push(row('lifetime', 'Presupuesto total', b.lifetime != null ? money(b.lifetime, c) : 'Sin presupuesto total'));
  } else if (b.level === 'adset') {
    if (b.daily != null) out.push(row('daily', 'Presupuesto diario (suma de los conjuntos)', money(b.daily, c)));
    if (b.lifetime != null) out.push(row('lifetime', 'Presupuesto total (suma de los conjuntos)', money(b.lifetime, c)));
  }
  if (b.remaining != null) {
    out.push(row('remaining', metaRemainingLabel(b.remainingScope), remainingText(b.remaining, c, b.remainingSource)));
  }
  if (b.spentInPeriod != null) out.push(row('period', 'Gastado en el período', money(b.spentInPeriod, c)));
  if (b.spentTotal != null) {
    const since = formatMetaDay(b.spentTotalSince);
    out.push(row('total', since ? `Gastado desde el inicio (${since})` : 'Gastado desde el inicio', money(b.spentTotal, c)));
  }
  if (full) {
    if (b.level === 'campaign' || b.level === 'adset') out.push(row('cap', 'Tope de gasto', b.spendCap != null ? money(b.spendCap, c) : 'Sin tope'));
    const bid = metaBidStrategyLabel(b.bidStrategy);
    if (bid) out.push(row('bid', 'Estrategia de puja', bid));
    const ends = formatMetaDay(b.endsAt, { withYear: true });
    if (ends) out.push(row('ends', 'Termina', ends));
  }
  return out;
}

/**
 * Barra del presupuesto total (27/10): `budget_used` de `lifetime`, con `pct_of_lifetime_used` como %
 * (cierra con el restante). Una API vieja sin `budget_used` muestra solo el %.
 */
function UsedBar({ pct, used = null, total = null, currency = null, testId = 'meta-budget-used' }) {
  const value = pct ?? (used != null && total ? Math.round((1000 * used) / total) / 10 : null);
  if (value == null) return null;
  const width = Math.max(0, Math.min(100, value));
  const pctText = formatMetaPct(value).replace(',0 %', ' %');
  const label = used != null && total != null
    ? `Usado del presupuesto total: ${money(used, currency)} de ${money(total, currency)} (${pctText})`
    : `Del presupuesto total ya se usó el ${pctText}`;
  return (
    <Box data-testid={testId}>
      <Typography sx={{ ...metaAdsMutedTextSx, fontSize: '12px', mb: 0.5 }}>{label}</Typography>
      <Box sx={{ height: 8, borderRadius: '4px', bgcolor: 'rgba(255,255,255,0.08)', overflow: 'hidden' }} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(width)} aria-label="Porcentaje del presupuesto total usado">
        <Box sx={{ height: '100%', width: `${width}%`, bgcolor: colors.primary, borderRadius: '4px' }} />
      </Box>
    </Box>
  );
}

/**
 * F2b · Bloque `write` de cada conjunto de la campaña (`getmetaadsets`, §7), por id. Solo se pide con
 * escritura visible; sin la ruta o con error → `{}` (los conjuntos se ven como antes, sin ⋮).
 */
function useAdSetWrites(campaign, detail, write) {
  const enabled = Boolean(write);
  const state = useMetaRead(
    (signal) => getMetaAdSets({ period: detail?.period, campaignId: campaign.id, includeInactive: true, ownerUserId: detail?.ownerUserId, signal }),
    { enabled, deps: [campaign.id, detail?.ownerUserId || '', write?.version || 0] },
  );
  return useMemo(() => {
    const list = Array.isArray(state.res?.data?.adsets) ? state.res.data.adsets : [];
    const map = {};
    list.forEach((a) => {
      const id = a?.adset_id == null ? '' : String(a.adset_id);
      const block = readMetaWriteBlock(a?.write);
      if (id && block) map[id] = block;
    });
    return map;
  }, [state.res]);
}

function AdSetBlock({ adset, currency, menuItems = null }) {
  const rows = [
    adset.daily != null ? row('daily', 'Presupuesto diario', money(adset.daily, currency))
      : adset.lifetime != null ? row('lifetime', 'Presupuesto total', money(adset.lifetime, currency))
        : row('budget', 'Presupuesto', 'De la campaña'),
    adset.remaining != null ? row('remaining', metaRemainingLabel(adset.remainingScope), remainingText(adset.remaining, currency, adset.remainingSource)) : null,
    adset.spentTotal != null ? row('spent', 'Gastado desde el inicio', money(adset.spentTotal, currency)) : null,
  ].filter(Boolean);
  const status = metaAdSetStatusLabel(adset.status);
  return (
    <Box component="li" data-testid={`meta-budget-adset-${adset.id}`}
      sx={{ borderLeft: `2px solid ${colors.primary}`, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '0 8px 8px 0', px: 1.25, py: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ ...metaAdsBodyTextSx, fontWeight: 600, fontSize: '13px', overflowWrap: 'anywhere' }}>{adset.name || 'Conjunto de anuncios'}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {status ? (
            <Box component="span" sx={{ ...metaAdsMutedTextSx, fontSize: '11px', px: 1, py: '1px', borderRadius: '999px', bgcolor: 'rgba(255,255,255,0.07)', whiteSpace: 'nowrap' }}>{status}</Box>
          ) : null}
          {menuItems && menuItems.length ? (
            <LinkMenu items={menuItems} label="Más acciones" testId={`meta-adset-${adset.id}-menu`} />
          ) : null}
        </Box>
      </Box>
      <MetaStatRows rows={rows} />
    </Box>
  );
}

/**
 * Presupuesto de UNA campaña (paso 2 del refactor: vive en el detalle de la campaña, sección
 * «Presupuesto»). Filas (diario / total / restante con su alcance / gastado), barra del total y sus
 * conjuntos. Sin chip de nivel (29/10): las filas ya dicen si es «suma de los conjuntos». No hace pedidos: `budget` ya viene en la campaña de `getmetacampaigns`.
 */
export function MetaCampaignBudgetDetail({ campaign, detail = null, write = null }) {
  const b = campaign.budget;
  const rows = b ? campaignBudgetRows(b, true) : [];
  // F2b: ⋮ de cada conjunto (mismas acciones que la campaña; el presupuesto, solo si vive en el conjunto).
  const adsetWrites = useAdSetWrites(campaign, detail, write);
  const adsetMenu = (a) => {
    if (!write) return null;
    const target = { id: a.id, name: a.name || 'Conjunto de anuncios', write: adsetWrites[a.id] || null, campaignId: campaign.id };
    const active = String(a.status || '').toUpperCase() === 'ACTIVE';
    return metaWriteItems(write, 'adset', target, active, { ...metaWriteBudgetOf('adset', a, b?.currency || campaign.currency), campaignId: campaign.id });
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid={`meta-budget-campaign-${campaign.id}`}>
      {!b || b.level == null ? (
        <MetaFootnote testId={`meta-budget-${campaign.id}-pending`}>Todavía no disponible: el presupuesto se completa solo en la próxima actualización diaria (6:00).</MetaFootnote>
      ) : (
        <>
          {rows.length ? <MetaStatRows rows={rows} testId={`meta-budget-${campaign.id}-rows`} /> : null}
          {b.level === 'none' ? <MetaFootnote>Meta no tiene un presupuesto cargado para esta campaña.</MetaFootnote> : null}
          {b.lifetime != null || b.pctUsed != null ? (
            <UsedBar pct={b.pctUsed} used={b.budgetUsed} total={b.lifetime} currency={b.currency} testId={`meta-budget-${campaign.id}-used`} />
          ) : null}
          {b.adsets.length ? (
            <>
              <Typography component="h4" sx={{ fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', fontWeight: 500 }}>
                {`Conjuntos de anuncios (${b.adsets.length})`}
              </Typography>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {b.adsets.map((a) => <AdSetBlock key={a.id} adset={a} currency={b.currency} menuItems={adsetMenu(a)} />)}
              </Box>
            </>
          ) : null}
        </>
      )}
    </Box>
  );
}

/**
 * Resumen del presupuesto por moneda (paso 2: va en el Informe, debajo del Resumen del espacio).
 * Una tarjeta por moneda (nunca se suman monedas); «Queda hoy (presupuestos diarios)» y
 * «Restante de presupuestos totales» ocultas con contador 0; barra usado / total. No hace pedidos:
 * suma las campañas de `getmetacampaigns` que recibe (las de la lista: siguen «Activas · Todas»).
 * `filterLabel` («Activas» / «Todas») rotula de qué lista sale.
 */
export function MetaBudgetSummaries({ campaigns, filterLabel = null }) {
  const summaries = useMemo(() => metaBudgetSummaries(campaigns || []), [campaigns]);
  if (!summaries.length) return null;
  const from = filterLabel ? ` Sigue el filtro de Campañas (${filterLabel}).` : '';
  return summaries.map((s) => (
    <MetaCard key={s.currency} title={`Resumen del presupuesto · ${s.currency}`} testId={`meta-budget-summary-${s.currency}`}>
      <MetaStatRows rows={summaryRows(s)} testId={`meta-budget-summary-${s.currency}-rows`} />
      {s.lifetimeCampaignsCount > 0 ? (
        <UsedBar pct={s.lifetimePctUsed} used={s.lifetimeUsedSum} total={s.lifetimeBudgetSum} currency={s.currency} testId={`meta-budget-summary-${s.currency}-used`} />
      ) : null}
      <MetaFootnote testId={`meta-budget-summary-${s.currency}-note`}>
        {(summaries.length > 1
          ? 'Cada moneda va por separado: nunca se suman monedas distintas.'
          : `Suma de ${s.campaignsCount === 1 ? 'la campaña que se muestra' : `las ${s.campaignsCount} campañas que se muestran`}.`) + from}
      </MetaFootnote>
    </MetaCard>
  ));
}
